import { CKPicker, CKSwitch } from '@clearkit/react';

import FacebookLogo from '~/images/destinations/Facebook.svg';
import { destinationTypeFacebookConversion } from '~/lib/destinations';
import { hasError } from '~/lib/graphql';
import { readableSingularSegmentType } from '~/lib/segments';
import Disconnected from '~/pages/destinations/components/Disconnected';
import TriggerType from '~/pages/destinations/components/TriggerType';

import ContentBox from '../../components/ContentBox';
import FieldLabel from '../../components/FieldLabel';
import FormContentWrapper from '../../components/FormContentWrapper';
import FormHeader from '../../components/FormHeader';
import MoneyInput from '../../components/MoneyInput';
import EventTypePicker from './EventTypePicker';

class SegmentDestinationNewFacebookConversion extends React.Component {
  state = {
    eventName: null,
    leadValue: null,
    leadValueCurrency: null,
    triggerType: 'segment_enter',
    facebookAdAccountId: null,
    facebookAdPixelId: null,
    limitedDataUse: true,
  };

  UNSAFE_componentWillMount() {
    if (this.props.destination) {
      this.setState({
        ...this.props.destination,
        ...this.props.destination.config,
      });
    }
  }

  handleFieldChange = (name, value) => {
    this.setState({ [name]: value });
  };

  render() {
    const {
      accountConnector,
      errors,
      destination,
      refetch,
      segment,
      destinationOptions,
      FormFooter,
    } = this.props;

    const {
      facebookCurrencyOptions,
      facebookEventTypeOptions,
    } = destinationOptions;

    const {
      eventName,
      leadValue,
      leadValueCurrency,
      triggerType,
      facebookAdAccountId,
      facebookAdPixelId,
      limitedDataUse,
    } = this.state;

    const facebookAdAccounts = accountConnector?.adAccounts || [];
    const facebookAdAccount = facebookAdAccounts.find((adAccount) => {
      return adAccount.id === facebookAdAccountId;
    });
    const facebookAdPixel = facebookAdAccount?.adPixels?.find(
      (pixel) => pixel.id === facebookAdPixelId,
    );
    const facebookAdPixelOptions =
      facebookAdAccount?.adPixels.map((pixel) => ({
        label: pixel.name,
        value: pixel.id,
      })) || [];

    const humanizedSegmentType = readableSingularSegmentType(segment.type);
    const destinationFlowDescription = destination
      ? 'Will send event to Facebook'
      : null;

    if (!accountConnector) {
      return (
        <Disconnected
          service="facebook"
          onSuccess={refetch}
          Logo={FacebookLogo}
          FormFooter={FormFooter}
        />
      );
    }

    return (
      <>
        <FormHeader
          destinationType={destinationTypeFacebookConversion}
          destinationName="Facebook"
          destinationFlowDescription={destinationFlowDescription}
          segmentName={segment.name}
          segmentCollection={segment.collection.name}
        />
        <FormContentWrapper>
          <h2 className="text-base font-medium text-gray-600">Trigger</h2>
          <span className="text-sm text-gray-600">
            Select the event that triggers this Destination to run.{' '}
            <a
              href="https://help.clearbit.com/hc/en-us/articles/360023453694"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline' }}>
              Learn more
            </a>
          </span>

          <TriggerType
            errors={errors}
            handleChange={this.handleFieldChange}
            triggerType={triggerType}
            triggerTypes={[
              {
                value: 'segment_enter',
                label: (
                  <span>
                    When a {humanizedSegmentType} <strong>enters</strong> the
                    segment
                  </span>
                ),
              },
            ]}
          />

          <h2 className="text-base font-medium text-gray-600">
            Configure Facebook Conversion
          </h2>

          <ContentBox>
            <div className="mb-4">
              <div className="flex mb-4">
                <div className="flex-1 mr-2">
                  <FieldLabel
                    for="facebookAccount-input"
                    label="Facebook account *"
                  />
                  <CKPicker
                    id="facebookAccount"
                    items={facebookAdAccounts.map((adAccount) => ({
                      label: adAccount.name,
                      value: adAccount.id,
                    }))}
                    itemToString={(item) => item.label}
                    placeholder="Choose a Facebook account"
                    variant={
                      hasError(errors, 'facebookAdAccountId') ? 'error' : null
                    }
                    selectedItem={
                      facebookAdAccount && {
                        label: facebookAdAccount.name,
                        value: facebookAdAccount.id,
                      }
                    }
                    onSelectedItemChange={({ selectedItem }) => {
                      this.handleFieldChange(
                        'facebookAdAccountId',
                        selectedItem.value,
                      );
                    }}
                  />
                </div>
                <div className="flex-1 ml-2">
                  <FieldLabel
                    for="facebookPixel-input"
                    label="Facebook pixel *"
                  />
                  <CKPicker
                    id="facebookPixel"
                    isDisabled={facebookAdPixelOptions.length === 0}
                    items={facebookAdPixelOptions}
                    variant={
                      hasError(errors, 'facebookAdPixelId') ? 'error' : null
                    }
                    itemToString={(item) => item.label}
                    placeholder={
                      facebookAdAccountId && facebookAdPixelOptions.length === 0
                        ? 'No Facebook Pixels for this Ad Account'
                        : 'Choose a Facebook Pixel'
                    }
                    selectedItem={
                      facebookAdPixel && {
                        label: facebookAdPixel.name,
                        value: facebookAdPixel.id,
                      }
                    }
                    onSelectedItemChange={({ selectedItem }) => {
                      this.handleFieldChange(
                        'facebookAdPixelId',
                        selectedItem.value,
                      );
                    }}
                  />
                </div>
              </div>

              <div className="flex w-1/2 mb-4">
                <div className="flex-1 mr-2">
                  <EventTypePicker
                    options={facebookEventTypeOptions}
                    error={hasError(errors, 'eventName')}
                    onChange={(value) =>
                      this.handleFieldChange('eventName', value)
                    }
                    selectedEventType={eventName}
                  />
                </div>
              </div>

              <div className="flex w-1/2">
                <div className="flex-1 mr-2">
                  <FieldLabel for="leadValue" label="Lead value" />
                  <MoneyInput
                    id="leadValue"
                    value={leadValue}
                    defaultValue={leadValue / 100}
                    onChange={(value) => {
                      this.handleFieldChange('leadValue', value);
                    }}
                    placeholder="Lead value"
                  />
                </div>
                <div className="flex-1 ml-2 mr-2">
                  <FieldLabel
                    for="valueCurrency-input"
                    label="Value currency"
                  />
                  <CKPicker
                    id="valueCurrency"
                    items={facebookCurrencyOptions.map((currency) => ({
                      label: currency,
                      value: currency,
                    }))}
                    itemToString={(item) => item.label}
                    placeholder="Value currency"
                    selectedItem={{
                      label: leadValueCurrency,
                      value: leadValueCurrency,
                    }}
                    onSelectedItemChange={({ selectedItem }) => {
                      this.handleFieldChange(
                        'leadValueCurrency',
                        selectedItem.value,
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="flex w-1/2">
              <div className="flex-1 mr-2">
                <FieldLabel for="ldu" label="Limited data usage" />
                <CKSwitch
                  id="ldu"
                  checked={limitedDataUse}
                  onChange={() => {
                    this.handleFieldChange('limitedDataUse', !limitedDataUse);
                  }}
                />
              </div>
            </div>
            <div className="text-sm italic text-gray-800">*Required</div>
          </ContentBox>
        </FormContentWrapper>
        <FormFooter
          destinationConfig={{
            config: {
              eventName: this.state.eventName,
              leadValue: this.state.leadValue,
              leadValueCurrency: this.state.leadValueCurrency,
              facebookAdAccountId: this.state.facebookAdAccountId,
              facebookAdPixelId: this.state.facebookAdPixelId,
              limitedDataUse: this.state.limitedDataUse,
            },
            triggerType: this.state.triggerType,
          }}
        />
      </>
    );
  }
}

SegmentDestinationNewFacebookConversion.propTypes = {
  destination: PropTypes.object,
  destinationOptions: PropTypes.object,
  errors: PropTypes.array.isRequired,
  refetch: PropTypes.func.isRequired,
  segment: PropTypes.object.isRequired,
};

export default SegmentDestinationNewFacebookConversion;
